<template>
  <el-card>
    <template #header>
      <div class="head">
        <span>角色管理</span>
        <el-button type="primary" @click="openCreate">新增角色</el-button>
      </div>
    </template>

    <el-table :data="roles" v-loading="loading" stripe>
      <el-table-column prop="role_id" label="ID" width="70" />
      <el-table-column prop="role_name" label="角色名称" width="180" />
      <el-table-column prop="role_code" label="角色编码" width="170" />
      <el-table-column prop="description" label="描述" />
      <el-table-column label="权限数" width="90">
        <template #default="{ row }">{{ row.permissions?.length || 0 }}</template>
      </el-table-column>
      <el-table-column label="系统角色" width="100">
        <template #default="{ row }">
          <el-tag v-if="row.is_system" type="warning">是</el-tag>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="340" fixed="right">
        <template #default="{ row }">
          <el-button size="small" @click="openEdit(row)">编辑</el-button>
          <el-button size="small" type="success" @click="openCopy(row)">复制权限</el-button>
          <el-button size="small" type="danger" @click="removeRole(row)" :disabled="row.is_system">删除</el-button>
        </template>
      </el-table-column>
    </el-table>
  </el-card>

  <el-dialog v-model="showDialog" :title="dialogTitle" width="760px">
    <el-form :model="form" label-width="100px">
      <el-form-item v-if="mode === 'create'" label="角色模板">
        <el-select v-model="selectedTemplateKey" clearable placeholder="可直接套用常见岗位权限" style="width: 100%" @change="applyTemplate">
          <el-option v-for="tpl in roleTemplates" :key="tpl.key" :label="tpl.label" :value="tpl.key" />
        </el-select>
      </el-form-item>
      <el-form-item label="角色名称">
        <el-input v-model="form.role_name" />
      </el-form-item>
      <el-form-item label="角色编码">
        <el-input v-model="form.role_code" />
      </el-form-item>
      <el-form-item label="描述">
        <el-input v-model="form.description" />
      </el-form-item>
      <el-form-item label="数据范围">
        <div class="scope-panel">
          <div
            v-for="item in scopeModules"
            :key="item.key"
            class="scope-card"
          >
            <div class="scope-card__title">{{ item.label }}</div>
            <div class="scope-card__desc">{{ item.description }}</div>
            <el-radio-group v-model="scopeSelections[item.key]" class="scope-card__radios">
              <el-radio-button label="all">全部</el-radio-button>
              <el-radio-button label="department">本部门</el-radio-button>
              <el-radio-button label="self">本人</el-radio-button>
            </el-radio-group>
          </div>
        </div>
      </el-form-item>
      <el-form-item label="权限点">
        <div class="perm-groups">
          <el-tabs v-model="activeModuleTab">
            <el-tab-pane v-for="module in moduleTabs" :key="module.key" :label="module.label" :name="module.key">
              <el-tabs v-model="activeUnitTabs[module.key]" class="inner-tabs">
                <el-tab-pane
                  v-for="unit in module.units"
                  :key="`${module.key}-${unit.key}`"
                  :label="unit.label"
                  :name="unit.key"
                >
                  <div class="perm-group">
                    <div class="perm-group__header">
                      <h4>{{ module.label }} / {{ unit.label }}</h4>
                      <el-checkbox
                        :model-value="isGroupAllChecked(unit)"
                        :indeterminate="isGroupIndeterminate(unit)"
                        @change="(checked) => toggleGroupPermissions(unit, checked)"
                      >
                        全选本单元
                      </el-checkbox>
                    </div>
                    <el-checkbox-group v-model="form.permissions" class="perm-check-grid">
                      <el-checkbox v-for="p in unit.items" :key="p.key" :label="p.key" class="perm-check-item">
                        <div class="perm-check-item__name">{{ p.name }}</div>
                        <div class="perm-check-item__key">{{ p.key }}</div>
                      </el-checkbox>
                    </el-checkbox-group>
                  </div>
                </el-tab-pane>
              </el-tabs>
            </el-tab-pane>
          </el-tabs>
        </div>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="showDialog = false">取消</el-button>
      <el-button type="primary" :loading="submitting" @click="submitRole">保存</el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { createRole, copyRole, deleteRole, getPermissionCatalog, getRoles, updateRole } from '@/api/rbac'
import { isRequestCanceledError } from '@/utils/request'

const loading = ref(false)
const submitting = ref(false)
const roles = ref([])
const permissionCatalog = ref({})
const showDialog = ref(false)
const mode = ref('create') // create | edit | copy
const editingId = ref(null)
const activeModuleTab = ref('')
const activeUnitTabs = reactive({})
const selectedTemplateKey = ref('')

const form = reactive({
  role_name: '',
  role_code: '',
  description: '',
  permissions: []
})

const roleTemplates = [
  {
    key: 'sales',
    label: '业务员模板',
    role_name: '业务员',
    role_code: 'SALES',
    description: '只看本人客户和本人客户订单，具备基础业务操作权限',
    permissions: [
      'customers.records.view',
      'customers.contacts.view',
      'customers.addresses.view',
      'orders.sales.view',
      'orders.material_matching.view',
      'orders.material_matching.edit',
      'orders.dispatch.view',
      'orders.delivery.view',
      'orders.color_confirmation.view',
      'orders.scope.self',
      'finance.receivables.view',
      'amount.view',
      'mobile.orders',
      'mobile.dispatch',
      'mobile.receivables'
    ]
  },
  {
    key: 'sales_manager',
    label: '业务经理模板',
    role_name: '业务经理',
    role_code: 'SALES_MANAGER',
    description: '可查看并管理本部门客户、订单及归属分配',
    permissions: [
      'customers.records.view',
      'customers.records.edit',
      'customers.contacts.view',
      'customers.contacts.edit',
      'customers.addresses.view',
      'customers.addresses.edit',
      'customers.assign_owner',
      'orders.sales.view',
      'orders.sales.edit',
      'orders.sales.review',
      'orders.material_matching.view',
      'orders.material_matching.edit',
      'orders.material_matching.review',
      'orders.dispatch.view',
      'orders.dispatch.edit',
      'orders.dispatch.review',
      'orders.delivery.view',
      'orders.delivery.edit',
      'orders.delivery.review',
      'orders.delivery.sync_price.request',
      'orders.color_confirmation.view',
      'orders.color_confirmation.edit',
      'orders.color_confirmation.review',
      'customers.scope.department',
      'orders.scope.department',
      'mobile.orders',
      'mobile.dispatch'
    ]
  },
  {
    key: 'purchaser',
    label: '采购员模板',
    role_name: '采购员',
    role_code: 'PURCHASER',
    description: '采购板块查看与编辑',
    permissions: [
      'purchase.suppliers.view',
      'purchase.orders.view',
      'purchase.orders.edit',
      'purchase.tracking.view',
      'purchase.tracking.edit',
      'purchase.processing_follow.view',
      'purchase.scope.self'
    ]
  },
  {
    key: 'processing_follow',
    label: '加工跟进（移动）',
    role_name: '加工跟进',
    role_code: 'PROCESSING_FOLLOW',
    description: '仅采购侧加工跟进列表与移动端跟进（不含配料编辑）',
    permissions: ['purchase.processing_follow.view', 'mobile.processing_follow']
  },
  {
    key: 'warehouse',
    label: '仓管模板',
    role_name: '仓管',
    role_code: 'WAREHOUSE',
    description: '库存板块查看与编辑（不含出入库审批；审批请用「出入库审批员」模板）',
    permissions: [
      'inventory.records.view',
      'inventory.records.edit',
      'inventory.operations.view',
      'inventory.operations.edit',
      'inventory.material_batches.view',
      'inventory.locations.view',
      'inventory.locations.edit',
      'inventory.manual_code_intake.view',
      'inventory.manual_code_intake.process',
      'inventory.scope.department',
      'tasks.view',
      'mobile.tasks',
      'mobile.inventory'
    ]
  },
  {
    key: 'inventory_approval',
    label: '出入库审批员',
    role_name: '出入库审批',
    role_code: 'INVENTORY_APPROVAL',
    description: '非常规出入库/期初入库审批（桌面+移动）；不含普通库存编辑',
    permissions: [
      'inventory.operation_approval.review',
      'inventory.operation_approval.initial_review',
      'mobile.inventory_operation_approval',
      'inventory.records.view',
      'inventory.operations.view'
    ]
  },
  {
    key: 'manual_code_intake',
    label: '盘点未知料号入库',
    role_name: '盘点未知料号入库',
    role_code: 'MANUAL_CODE_INTAKE',
    description: '仅处理盘点「未知仓库料号入库申请」（查看+确认入库/驳回），不含整块库存编辑',
    permissions: [
      'inventory.manual_code_intake.view',
      'inventory.manual_code_intake.process',
      'inventory.material_batches.view',
      'tasks.view'
    ]
  },
  {
    key: 'delivery_sync_price',
    label: '送货单价/染费同步审批',
    role_name: '送货单价/染费同步审批',
    role_code: 'DELIVERY_SYNC_PRICE',
    description: '任务中心「送货单价/染费同步」待办、桌面/移动审批页；可审批并将送货单价与染费回写订单',
    permissions: [
      'orders.delivery.sync_price.review',
      'orders.delivery.view',
      'tasks.view',
      'mobile.tasks'
    ]
  },
  {
    key: 'finance',
    label: '财务模板',
    role_name: '财务',
    role_code: 'FINANCE',
    description: '财务、金额、单价查看与编辑；含应收与应付账单',
    permissions: [
      'finance.view',
      'finance.edit',
      'billing.statements.view',
      'billing.statements.edit',
      'billing.statements.review',
      'billing.statements.payable.view',
      'billing.statements.payable.edit',
      'billing.statements.payable.review',
      'billing.prepayment.view',
      'billing.prepayment.edit',
      'billing.document_center.view',
      'billing.payment_footer_accounts.view',
      'billing.payment_footer_accounts.edit',
      'billing.tax_invoices.view',
      'billing.tax_invoices.edit',
      'billing.tax_invoices.review',
      'pricing.view',
      'pricing.edit',
      'amount.view',
      'amount.edit'
    ]
  },
  {
    key: 'billing_ar',
    label: '业务应收（账单）',
    role_name: '业务应收',
    role_code: 'BILLING_AR',
    description: '仅应收账单：建应收、维护明细；不含应付与整块财务',
    permissions: [
      'billing.statements.view',
      'billing.statements.edit',
      'billing.statements.review',
      'amount.view'
    ]
  }
]

const scopeModules = [
  {
    key: 'customers',
    label: '客户板块',
    description: '控制客户资料的可见范围'
  },
  {
    key: 'orders',
    label: '订单板块',
    description: '控制销售订单按客户归属的可见范围'
  },
  {
    key: 'purchase',
    label: '采购板块',
    description: '控制采购单按创建人或部门的可见范围'
  },
  {
    key: 'inventory',
    label: '库存板块',
    description: '控制库存记录按创建人或部门的可见范围'
  }
]

const scopeSelections = reactive({
  customers: 'all',
  orders: 'all',
  purchase: 'all',
  inventory: 'all'
})

const dialogTitle = computed(() => {
  if (mode.value === 'edit') return '编辑角色'
  if (mode.value === 'copy') return '复制角色权限'
  return '新增角色'
})

const groupLabelMap = {
  system: '系统板块',
  customers: '客户板块',
  masterdata: '基础资料板块',
  orders: '订单板块',
  purchase: '采购板块',
  inventory: '库存板块',
  finance: '财务板块',
  mobile: '移动工作台',
  tasks: '任务中心',
  quality: '质量板块'
}

const moduleTabs = computed(() => {
  const modules = []
  const catalog = permissionCatalog.value
  if (!catalog || typeof catalog !== 'object' || Array.isArray(catalog)) {
    return modules
  }
  for (const [moduleKey, items] of Object.entries(catalog)) {
    const unitMap = {}
    for (const item of (items || []).filter(
      (entry) => entry && typeof entry === 'object' && entry.action !== 'scope'
    )) {
      const unitKey = item.unit || '默认单元'
      if (!unitMap[unitKey]) {
        unitMap[unitKey] = { key: unitKey, label: unitKey, items: [] }
      }
      unitMap[unitKey].items.push(item)
    }
    const units = Object.values(unitMap)
    modules.push({
      key: moduleKey,
      label: groupLabelMap[moduleKey] || moduleKey,
      units
    })
  }
  return modules
})

const isGroupAllChecked = (group) => {
  if (!group?.items?.length) return false
  return group.items.every(item => form.permissions.includes(item.key))
}

const isGroupIndeterminate = (group) => {
  if (!group?.items?.length) return false
  const checkedCount = group.items.filter(item => form.permissions.includes(item.key)).length
  return checkedCount > 0 && checkedCount < group.items.length
}

const resetScopeSelections = () => {
  scopeModules.forEach((item) => {
    scopeSelections[item.key] = 'all'
  })
}

const fillScopeSelectionsFromPermissions = (permissions) => {
  resetScopeSelections()
  const keySet = new Set(permissions || [])
  scopeModules.forEach((item) => {
    if (keySet.has(`${item.key}.scope.self`)) {
      scopeSelections[item.key] = 'self'
    } else if (keySet.has(`${item.key}.scope.department`)) {
      scopeSelections[item.key] = 'department'
    }
  })
}

const buildPermissionsWithScopes = () => {
  const nextPermissions = new Set(
    (form.permissions || []).filter((key) => !/^(customers|orders|purchase|inventory)\.scope\./.test(key))
  )
  scopeModules.forEach((item) => {
    const selectedScope = scopeSelections[item.key]
    if (selectedScope === 'self' || selectedScope === 'department') {
      nextPermissions.add(`${item.key}.scope.${selectedScope}`)
    }
  })
  return Array.from(nextPermissions)
}

const toggleGroupPermissions = (group, checked) => {
  const keySet = new Set(form.permissions)
  for (const item of (group.items || [])) {
    if (checked) keySet.add(item.key)
    else keySet.delete(item.key)
  }
  form.permissions = Array.from(keySet)
}

const applyTemplate = (templateKey) => {
  const tpl = roleTemplates.find(item => item.key === templateKey)
  if (!tpl) return
  form.role_name = tpl.role_name
  form.role_code = tpl.role_code
  form.description = tpl.description
  form.permissions = [...tpl.permissions]
  fillScopeSelectionsFromPermissions(form.permissions)
}

const loadAll = async () => {
  loading.value = true
  try {
    const [r1, r2] = await Promise.all([getRoles(), getPermissionCatalog()])
    if (r1?.success) {
      roles.value = Array.isArray(r1.data) ? r1.data : []
    } else if (r1 && r1.success === false) {
      ElMessage.error(r1.message || '加载角色列表失败')
    }
    if (r2?.success) {
      const data = r2.data
      permissionCatalog.value =
        data && typeof data === 'object' && !Array.isArray(data) ? data : {}
      const firstModule = moduleTabs.value[0]
      if (firstModule) {
        activeModuleTab.value = firstModule.key
        for (const module of moduleTabs.value) {
          if (module.units?.length) {
            activeUnitTabs[module.key] = module.units[0].key
          }
        }
      }
    } else if (r2 && r2.success === false) {
      ElMessage.error(r2.message || '加载权限目录失败')
    }
  } catch (err) {
    if (isRequestCanceledError(err)) return
    ElMessage.error(err.response?.data?.message || '加载角色权限失败')
  } finally {
    loading.value = false
  }
}

const resetForm = () => {
  form.role_name = ''
  form.role_code = ''
  form.description = ''
  form.permissions = []
  resetScopeSelections()
  editingId.value = null
  selectedTemplateKey.value = ''
}

const openCreate = () => {
  mode.value = 'create'
  resetForm()
  showDialog.value = true
}

const openEdit = (row) => {
  mode.value = 'edit'
  form.role_name = row.role_name || ''
  form.role_code = row.role_code || ''
  form.description = row.description || ''
  form.permissions = [...(row.permissions || [])]
  fillScopeSelectionsFromPermissions(form.permissions)
  editingId.value = row.role_id
  showDialog.value = true
}

const openCopy = (row) => {
  mode.value = 'copy'
  form.role_name = `${row.role_name}_复制`
  form.role_code = `${row.role_code || 'role'}_copy`
  form.description = `复制自 ${row.role_name}`
  form.permissions = [...(row.permissions || [])]
  fillScopeSelectionsFromPermissions(form.permissions)
  editingId.value = row.role_id
  showDialog.value = true
}

const submitRole = async () => {
  if (!form.role_name.trim()) {
    ElMessage.error('角色名称不能为空')
    return
  }
  submitting.value = true
  try {
    let resp
    const normalizedPermissions = buildPermissionsWithScopes()
    const payload = {
      role_name: form.role_name.trim(),
      role_code: form.role_code.trim(),
      description: form.description,
      permissions: normalizedPermissions
    }
    if (mode.value === 'edit') {
      resp = await updateRole(editingId.value, payload)
    } else if (mode.value === 'copy') {
      resp = await copyRole(editingId.value, {
        role_name: payload.role_name,
        role_code: payload.role_code
      })
    } else {
      resp = await createRole(payload)
    }
    if (resp.success) {
      ElMessage.success('保存成功')
      showDialog.value = false
      await loadAll()
    } else {
      ElMessage.error(resp.message || '保存失败')
    }
  } catch (err) {
    ElMessage.error(err.response?.data?.message || '保存失败')
  } finally {
    submitting.value = false
  }
}

const removeRole = async (row) => {
  try {
    await ElMessageBox.confirm(`确定删除角色「${row.role_name}」吗？`, '提示', { type: 'warning' })
    const resp = await deleteRole(row.role_id)
    if (resp.success) {
      ElMessage.success('已删除')
      await loadAll()
    } else {
      ElMessage.error(resp.message || '删除失败')
    }
  } catch (err) {
    if (err !== 'cancel') ElMessage.error(err.response?.data?.message || '删除失败')
  }
}

onMounted(loadAll)

defineExpose({
  reload: loadAll
})
</script>

<style scoped>
.head { display: flex; justify-content: space-between; align-items: center; }
.perm-groups { width: 100%; max-height: 360px; overflow: auto; border: 1px solid #ebeef5; padding: 8px 12px; border-radius: 6px; }
.perm-group { margin-bottom: 12px; }
.perm-group h4 { margin: 6px 0; color: #606266; }
.perm-group__header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.perm-check-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 8px 10px; }
.perm-check-item { margin-right: 0; }
.perm-check-item__name { color: #303133; line-height: 1.4; }
.perm-check-item__key { color: #909399; font-size: 12px; line-height: 1.2; }
.scope-panel { width: 100%; display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 12px; }
.scope-card { border: 1px solid #ebeef5; border-radius: 8px; padding: 12px; background: #fafafa; }
.scope-card__title { font-weight: 600; color: #303133; margin-bottom: 6px; }
.scope-card__desc { color: #909399; font-size: 12px; margin-bottom: 10px; line-height: 1.5; }
.scope-card__radios { display: flex; flex-wrap: wrap; gap: 8px; }
</style>
